import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Landmark, X, Upload, ArrowLeftRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney, todayISO } from '../lib/format';
import { postBankTransfer } from '../lib/posting';
import type { Account, BankAccount } from '../lib/types';

interface RowWithBalance extends BankAccount {
  account_code: string;
  account_name: string;
  balance: number;
  unreconciled: number;
}

const SA_BANKS = ['FNB', 'Standard Bank', 'ABSA', 'Nedbank', 'Capitec', 'Investec', 'TymeBank', 'Discovery Bank', 'Bank Zero', 'Other'];

export default function BankAccounts() {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState<RowWithBalance[]>([]);
  const [creating, setCreating] = useState(false);
  const [transferring, setTransferring] = useState(false);

  async function refresh() {
    if (!currentOrg) return;
    const { data: bas } = await supabase
      .from('dodobooks_bank_accounts')
      .select('*, account:dodobooks_accounts(code, name)')
      .eq('org_id', currentOrg.id)
      .order('bank_name');
    const ids = (bas ?? []).map((b: any) => b.id);
    const { data: unrec } = ids.length
      ? await supabase
          .from('dodobooks_bank_transactions')
          .select('bank_account_id, id', { count: 'exact' })
          .in('bank_account_id', ids)
          .eq('reconciled', false)
      : { data: [] };
    const unrecCounts: Record<string, number> = {};
    for (const r of (unrec ?? []) as any[]) unrecCounts[r.bank_account_id] = (unrecCounts[r.bank_account_id] ?? 0) + 1;
    const { data: bal } = await supabase
      .from('dodobooks_account_balances')
      .select('account_id, balance')
      .eq('org_id', currentOrg.id);
    const balMap: Record<string, number> = {};
    for (const b of (bal ?? []) as any[]) balMap[b.account_id] = Number(b.balance ?? 0);

    setRows(((bas ?? []) as any[]).map((b) => ({
      ...b,
      account_code: b.account?.code ?? '',
      account_name: b.account?.name ?? '',
      balance: balMap[b.account_id] ?? 0,
      unreconciled: unrecCounts[b.id] ?? 0,
    })));
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentOrg]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">Bank accounts</h1>
          <p className="text-text-dim text-sm mt-1">Cash and credit accounts. Connect to your books and import statements.</p>
        </div>
        <div className="flex gap-2">
          {rows.length >= 2 && (
            <button onClick={() => setTransferring(true)} className="btn btn-secondary"><ArrowLeftRight size={14} /> Transfer</button>
          )}
          <Link to="/bank/import" className="btn btn-secondary"><Upload size={14} /> Import statement</Link>
          <button onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={14} /> Add bank account</button>
        </div>
      </header>

      {rows.length === 0 ? (
        <div className="card text-center py-12">
          <Landmark size={28} className="mx-auto text-text-mute mb-3" />
          <p className="text-text-dim text-sm mb-4">No bank accounts yet.</p>
          <button onClick={() => setCreating(true)} className="btn btn-primary">Add your first bank account</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {rows.map((b) => (
            <Link key={b.id} to={`/bank/${b.id}`} className="card card-hover">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-serif text-lg font-semibold">{b.bank_name}</div>
                  <div className="text-xs text-text-mute font-mono">
                    {b.account_code} · {b.account_name} {b.account_number ? `· ${b.account_number}` : ''}
                  </div>
                </div>
                <span className="pill pill-paid">{b.account_type}</span>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-text-mute font-mono">Balance</div>
                  <div className="font-serif text-2xl font-semibold text-emerald num">{formatMoney(b.balance, b.currency)}</div>
                </div>
                {b.unreconciled > 0 && (
                  <span className="text-xs text-gold font-mono">{b.unreconciled} unreconciled</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {creating && <CreateBankAccountDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); refresh(); }} />}
      {transferring && <TransferDialog banks={rows} onClose={() => setTransferring(false)} onPosted={() => { setTransferring(false); refresh(); }} />}
    </div>
  );
}

function TransferDialog({ banks, onClose, onPosted }: { banks: RowWithBalance[]; onClose: () => void; onPosted: () => void }) {
  const { currentOrg } = useOrg();
  const [fromId, setFromId] = useState(banks[0]?.id ?? '');
  const [toId, setToId] = useState(banks[1]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg) return;
    if (fromId === toId) { setErr('Source and destination must differ.'); return; }
    const from = banks.find((b) => b.id === fromId);
    const to = banks.find((b) => b.id === toId);
    if (!from || !to) { setErr('Pick valid bank accounts.'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a positive amount.'); return; }
    setBusy(true); setErr(null);
    const result = await postBankTransfer({
      org_id: currentOrg.id,
      from_bank: from,
      to_bank: to,
      amount: amt,
      date,
      reference: reference || null,
      notes: notes || null,
    });
    setBusy(false);
    if (result.error) setErr(result.error);
    else onPosted();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="h-section">Transfer between accounts</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="field col-span-2">
            <label>From *</label>
            <select required value={fromId} onChange={(e) => setFromId(e.target.value)}>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ` · ${b.account_number}` : ''} — {formatMoney(b.balance, b.currency)}</option>)}
            </select>
          </div>
          <div className="field col-span-2">
            <label>To *</label>
            <select required value={toId} onChange={(e) => setToId(e.target.value)}>
              {banks.filter((b) => b.id !== fromId).map((b) => <option key={b.id} value={b.id}>{b.bank_name}{b.account_number ? ` · ${b.account_number}` : ''} — {formatMoney(b.balance, b.currency)}</option>)}
            </select>
          </div>
          <div className="field"><label>Date *</label><input required type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="field"><label>Amount *</label><input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" /></div>
          <div className="field col-span-2"><label>Reference</label><input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="EFT reference (optional)" /></div>
          <div className="field col-span-2"><label>Notes</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50">
              <ArrowLeftRight size={14} /> {busy ? 'Posting…' : 'Post transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateBankAccountDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { currentOrg } = useOrg();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bankName, setBankName] = useState('FNB');
  const [accountTypeKind, setAccountTypeKind] = useState<'cheque' | 'savings' | 'credit_card'>('cheque');
  const [accountNumber, setAccountNumber] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!currentOrg) return;
    supabase
      .from('dodobooks_accounts')
      .select('*')
      .eq('org_id', currentOrg.id)
      .eq('is_bank', true)
      .eq('is_archived', false)
      .order('code')
      .then(({ data }) => {
        setAccounts((data ?? []) as Account[]);
        const first = (data ?? [])[0] as any;
        if (first) setGlAccountId(first.id);
      });
  }, [currentOrg]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentOrg) return;
    if (!glAccountId) { setErr('Pick the GL account this bank account maps to.'); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.from('dodobooks_bank_accounts').insert({
      org_id: currentOrg.id,
      account_id: glAccountId,
      bank_name: bankName,
      account_type: accountTypeKind,
      account_number: accountNumber || null,
      branch_code: branchCode || null,
      currency: 'ZAR',
      is_active: true,
    });
    setBusy(false);
    if (error) setErr(error.message);
    else onCreated();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="h-section">Add bank account</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="field"><label>Bank</label>
            <select value={bankName} onChange={(e) => setBankName(e.target.value)}>
              {SA_BANKS.map((b) => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div className="field"><label>Account type</label>
            <select value={accountTypeKind} onChange={(e) => setAccountTypeKind(e.target.value as any)}>
              <option value="cheque">Cheque / current</option>
              <option value="savings">Savings</option>
              <option value="credit_card">Credit card</option>
            </select>
          </div>
          <div className="field"><label>Account number</label><input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} /></div>
          <div className="field"><label>Branch code</label><input value={branchCode} onChange={(e) => setBranchCode(e.target.value)} /></div>
          <div className="field col-span-2">
            <label>GL account (where movements post)</label>
            <select value={glAccountId} onChange={(e) => setGlAccountId(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
            <p className="text-[11px] text-text-mute mt-1">Pick 1000 Bank for a current account, 1100 Petty cash for cash, 2400 Credit card for credit.</p>
          </div>
          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50">{busy ? 'Adding…' : 'Add bank account'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
